import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Spinner,
  useConfirm,
  useToast,
} from '@aurora/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { api } from '../api/client.js';

interface Role {
  id: string;
  key: string;
  name: string;
  isSystem: boolean;
  permissions: string[];
  userCount: number;
}
interface PermissionDefinition {
  key: string;
  description: string;
}
interface RolesResponse {
  roles: Role[];
  availablePermissions: PermissionDefinition[];
}

/** Namensraum eines Permission-Keys (`crm.deals.read` → `crm`) als Gruppenlabel. */
const GROUP_LABELS: Record<string, string> = {
  core: 'System',
  crm: 'CRM',
  accounting: 'Buchhaltung',
  sales: 'Vertrieb',
  projects: 'Projekte',
  hr: 'Personal',
  inventory: 'Lager',
  catalog: 'Artikel',
  calendar: 'Kalender',
};

function groupOf(key: string): string {
  return key.split('.')[0] ?? 'sonstige';
}

export default function RolesPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmDialog = useConfirm();

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<Set<string> | null>(null);
  const [newRole, setNewRole] = useState({ key: '', name: '' });

  const rolesQuery = useQuery({
    queryKey: ['roles'],
    queryFn: () => api<RolesResponse>('/api/v1/roles'),
  });

  const roles = rolesQuery.data?.roles ?? [];
  const selected = roles.find((role) => role.key === selectedKey) ?? null;
  const hasFullAccess = selected?.permissions.includes('*') ?? false;

  // Permissions nach Namensraum gruppieren — sonst ist die Liste unbedienbar.
  const groups = useMemo(() => {
    const map = new Map<string, PermissionDefinition[]>();
    for (const definition of rolesQuery.data?.availablePermissions ?? []) {
      const group = groupOf(definition.key);
      map.set(group, [...(map.get(group) ?? []), definition]);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rolesQuery.data?.availablePermissions]);

  const current = draft ?? new Set(selected?.permissions ?? []);
  const dirty = draft !== null;

  const selectRole = (role: Role) => {
    setSelectedKey(role.key);
    setDraft(null);
  };

  const toggle = (key: string, on: boolean) => {
    const next = new Set(current);
    if (on) next.add(key);
    else next.delete(key);
    setDraft(next);
  };

  const toggleGroup = (definitions: PermissionDefinition[], on: boolean) => {
    const next = new Set(current);
    for (const definition of definitions) {
      if (on) next.add(definition.key);
      else next.delete(definition.key);
    }
    setDraft(next);
  };

  const save = useMutation({
    mutationFn: () =>
      api(`/api/v1/roles/${selectedKey}/permissions`, {
        method: 'PUT',
        body: { permissions: [...current] },
      }),
    onSuccess: () => {
      toast.success('Rechte gespeichert');
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (error) => toast.error(error.message),
  });

  const create = useMutation({
    mutationFn: () =>
      api('/api/v1/roles', { method: 'POST', body: { key: newRole.key, name: newRole.name } }),
    onSuccess: () => {
      toast.success('Rolle angelegt');
      setNewRole({ key: '', name: '' });
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (key: string) => api(`/api/v1/roles/${key}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Rolle gelöscht');
      setSelectedKey(null);
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (error) => toast.error(error.message),
  });

  if (rolesQuery.isLoading) {
    return (
      <div className="page">
        <Spinner label="Rollen werden geladen …" />
      </div>
    );
  }
  if (rolesQuery.isError) {
    return (
      <div className="page">
        <h1 className="page__title">Rollen &amp; Rechte</h1>
        <EmptyState
          title="Kein Zugriff"
          description="Dir fehlt die Berechtigung „Rollen verwalten“."
        />
      </div>
    );
  }

  return (
    <div className="page">
      <h1 className="page__title">Rollen &amp; Rechte</h1>
      <p className="page__lead">
        Lege fest, was eine Rolle darf. Wer welche Rolle hat, stellst du unter „Benutzer“ ein.
      </p>

      <div className="crm-detail__grid">
        <Card>
          <div className="crm-section__title">Rollen</div>
          <table className="au-table">
            <thead>
              <tr>
                <th>Rolle</th>
                <th>Mitglieder</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr
                  key={role.key}
                  onClick={() => selectRole(role)}
                  style={{
                    cursor: 'pointer',
                    fontWeight: role.key === selectedKey ? 600 : undefined,
                  }}
                >
                  <td>
                    {role.name} {role.isSystem && <Badge>System</Badge>}{' '}
                    {role.permissions.includes('*') && <Badge>Vollzugriff</Badge>}
                    <div className="crm-timeline__meta">{role.key}</div>
                  </td>
                  <td>{role.userCount}</td>
                  <td style={{ textAlign: 'right' }}>
                    {!role.isSystem && (
                      <Button
                        size="sm"
                        variant="subtle"
                        onClick={(event) => {
                          event.stopPropagation();
                          void confirmDialog({
                            title: `Rolle „${role.name}“ löschen?`,
                            description:
                              role.userCount > 0
                                ? `${role.userCount} Mitglied(er) verlieren die damit verbundenen Rechte sofort.`
                                : 'Die Rolle wird dauerhaft entfernt.',
                            confirmLabel: 'Löschen',
                            danger: true,
                          }).then((ok) => {
                            if (ok) remove.mutate(role.key);
                          });
                        }}
                      >
                        Löschen
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <form
            className="crm-form"
            style={{ marginTop: 'var(--au-space-4)' }}
            onSubmit={(event) => {
              event.preventDefault();
              if (newRole.key && newRole.name) create.mutate();
            }}
          >
            <div className="crm-form__row">
              <Field label="Schlüssel">
                {(id) => (
                  <Input
                    id={id}
                    required
                    placeholder="z. B. buchhaltung"
                    value={newRole.key}
                    onChange={(e) => setNewRole({ ...newRole, key: e.target.value })}
                  />
                )}
              </Field>
              <Field label="Anzeigename">
                {(id) => (
                  <Input
                    id={id}
                    required
                    placeholder="z. B. Buchhaltung"
                    value={newRole.name}
                    onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                  />
                )}
              </Field>
            </div>
            <div className="crm-form__actions">
              <Button type="submit" size="sm" loading={create.isPending}>
                Rolle anlegen
              </Button>
            </div>
          </form>
        </Card>

        <Card>
          {!selected ? (
            <EmptyState
              title="Keine Rolle gewählt"
              description="Wähle links eine Rolle, um ihre Rechte zu bearbeiten."
            />
          ) : hasFullAccess ? (
            <>
              <div className="crm-section__title">{selected.name}</div>
              <p className="crm-timeline__meta">
                Diese Rolle hat <strong>Vollzugriff</strong> auf alles. Der Vollzugriff kann ihr
                nicht entzogen werden — sonst könnte sich der Mandant aussperren.
              </p>
            </>
          ) : (
            <>
              <div className="crm-detail__header">
                <div className="crm-section__title">Rechte: {selected.name}</div>
                <Button
                  size="sm"
                  disabled={!dirty}
                  loading={save.isPending}
                  onClick={() => save.mutate()}
                >
                  Speichern
                </Button>
              </div>
              {groups.map(([group, definitions]) => {
                const all = definitions.every((d) => current.has(d.key));
                return (
                  <div key={group} style={{ marginBottom: 'var(--au-space-4)' }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '.5rem',
                        fontWeight: 600,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={all}
                        onChange={(e) => toggleGroup(definitions, e.target.checked)}
                      />
                      {GROUP_LABELS[group] ?? group}
                    </label>
                    <div style={{ paddingLeft: '1.5rem', marginTop: '.25rem' }}>
                      {definitions.map((definition) => (
                        <label
                          key={definition.key}
                          style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}
                        >
                          <input
                            type="checkbox"
                            checked={current.has(definition.key)}
                            onChange={(e) => toggle(definition.key, e.target.checked)}
                          />
                          <span>{definition.description}</span>
                          <span className="crm-timeline__meta">{definition.key}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
